import type { RouteLocationNormalized } from 'vue-router'

export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  // Here we have already checked the auth status via the plugin: auth/plugins/auth.client.ts
  // No need to re-check! Just verify they're logged in to access this page
  const auth = useAuth()
  const { env } = useRuntimeConfig().public

  /**
   * Nuxt Studio preview mode
   */
  if ((to.path.startsWith('/academy') && to.query.preview) || useCookie('previewToken').value) {
    return
  }

  if (to.path.includes('/academy') && env === 'staging') {
    return
  }

  const isLoggedIn = await auth.waitForAuth({ from: 'auth middleware' })
  if (isLoggedIn) {
    auth.debug('[auth middleware] user is logged in; to:', to.path, 'from:', from.path)

    // If the route contains a projectId parameter, we validate that the user has access to it
    // We do this in the middleware because redirecting from elsewhere in the auth dance is fraught
    const urlProjectId = Array.isArray(to.params.projectId) ? to.params.projectId[0] : to.params.projectId
    if (urlProjectId && auth.project) {
      // Check if user has access to the project in the URL
      const { project: urlProject } = auth.findProjectAndOrg(urlProjectId)

      if (!urlProject) {
        // User doesn't have access to the project in URL, redirect to their default project
        const defaultProject = auth.project
        // Only redirect if the default project is different from the URL project
        if (defaultProject.id !== urlProjectId) {
          const newPath = to.path.replace(`/project/${urlProjectId}`, `/project/${defaultProject.id}`)
          auth.debug('[auth middleware] redirecting from invalid project ID', {
            from: to.path,
            to: newPath,
            urlProjectId,
            defaultProjectId: defaultProject.id,
          })
          return navigateTo(newPath, { replace: true })
        }
      }
    }

    return
  }

  if (to.path.startsWith('/login')) {
    auth.debug('[auth middleware] already on login page; to:', to.path, 'from:', from.path)
    return
  }

  const reason = auth.wasPreviouslyLoggedIn ? 'expired' : undefined
  await auth.clear()
  auth.setRedirectTo(to.fullPath)
  auth.debug('[auth middleware] not logged in, redirecting to login. wasPreviouslyLoggedIn', auth.wasPreviouslyLoggedIn, 'to:', to.path, 'from:', from.path)
  return auth.navigateToLogin({ from: 'auth middleware' }, reason)
})
